import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/Button';
import Modal from '../../../../components/Modal';
import TableBox from '../../../../components/TableBox';
import TaskModal from './TaskModal';
import TaskRow from './TaskRow';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { buildHeadCells } from '../../../../utils/methods';
import { SortOrder } from '../../../../enums';
import { Task, TaskFormData } from '../../../../interfaces/property-visit.interface';
import { Validation } from '../../../../interfaces/common';

interface TasksTabProps {
  tasks: Task[];
  isEditable: boolean;
  taskModalOpen: boolean;
  taskDeleteModalOpen: boolean;
  editingTask: Task | null;
  taskFormData: TaskFormData;
  taskValidation: Validation;
  taskLoading: boolean;
  taskDropdownSearchData: { category: string; priority: string; status: string };
  allCategoryOptions: any[];
  categoryOptions: any[];
  allPriorityOptions: any[];
  priorityOptions: any[];
  allStatusOptions: any[];
  statusOptions: any[];
  onOpenAddTask: () => void;
  onOpenEditTask: (task: Task) => void;
  onCloseTaskModal: () => void;
  onTaskFieldChange: (e: any, name: string) => void;
  onTaskFieldBlur: (e: any, name: string) => void;
  onTaskDropdownSearchChange: (e: any, name: string) => void;
  onSaveTask: () => void;
  onOpenDeleteTask: (taskId: string) => void;
  onCloseDeleteTask: () => void;
  onConfirmDeleteTask: () => void;
}

const TasksTab: React.FC<TasksTabProps> = ({
  tasks, isEditable,
  taskModalOpen, taskDeleteModalOpen, editingTask,
  taskFormData, taskValidation, taskLoading,
  taskDropdownSearchData,
  allCategoryOptions, categoryOptions, allPriorityOptions, priorityOptions, allStatusOptions, statusOptions,
  onOpenAddTask, onOpenEditTask, onCloseTaskModal,
  onTaskFieldChange, onTaskFieldBlur, onTaskDropdownSearchChange, onSaveTask,
  onOpenDeleteTask, onCloseDeleteTask, onConfirmDeleteTask,
}) => {
  const { t } = useTranslation();

  const headCells = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.taskDescription,
      VALIDATION_FIELD_MAPPING.taskCategory,
      VALIDATION_FIELD_MAPPING.taskPriority,
      VALIDATION_FIELD_MAPPING.taskStatus,
      VALIDATION_FIELD_MAPPING.taskDueDate,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'propertyVisitTaskList'
  );

  const headerSection = (
    <div className="col-md-12 header-section-div">
      <div className="row">
        <div className="col-sm-12 px-3 py-5 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className="ri-task-line ri-26px"></i>
            <h5 className="mx-3 my-0">
              {t('propertyVisits.addEditView.pageTexts.tabs.tasks')} ({tasks.length})
            </h5>
          </div>
          {isEditable && (
            <Button
              className="btn btn-primary waves-effect waves-light"
              text={t('propertyVisits.addEditView.pageTexts.tasks.addTask')}
              icon={<i className="ri-add-line"></i>}
              onClick={onOpenAddTask}
            />
          )}
        </div>
      </div>
    </div>
  );

  const tableValues = useMemo(() => ({
    headCells,
    row: TaskRow,
    data: tasks,
    rowProps: { isEditable, onOpenEditTask, onOpenDeleteTask },
    headerSection,
    pagination: false,
    order: SortOrder.asc,
    orderBy: '',
    page: 1,
    perPage: 100,
    total: tasks.length,
    loading: false,
    setPage: () => {},
    setPerPage: () => {},
    setOrder: () => {},
    setOrderBy: () => {},
    noDataFoundMessage: t('propertyVisits.addEditView.pageTexts.tasks.emptyState'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tasks, isEditable, onOpenEditTask, onOpenDeleteTask, headCells, t]);

  return (
    <div className="card-body">
      <TableBox tableValues={tableValues} />

      <TaskModal
        open={taskModalOpen}
        editingTask={editingTask}
        formData={taskFormData}
        validation={taskValidation}
        loading={taskLoading}
        taskDropdownSearchData={taskDropdownSearchData}
        allCategoryOptions={allCategoryOptions}
        categoryOptions={categoryOptions}
        allPriorityOptions={allPriorityOptions}
        priorityOptions={priorityOptions}
        allStatusOptions={allStatusOptions}
        statusOptions={statusOptions}
        onClose={onCloseTaskModal}
        onFieldChange={onTaskFieldChange}
        onFieldBlur={onTaskFieldBlur}
        onTaskDropdownSearchChange={onTaskDropdownSearchChange}
        onSave={onSaveTask}
      />

      <Modal
        open={taskDeleteModalOpen}
        maxWidth="sm"
        title={t('propertyVisits.addEditView.pageTexts.tasks.modal.deleteTitle')}
        content={t('propertyVisits.addEditView.pageTexts.tasks.modal.deleteContent')}
        contentClassName="text-center"
        onClose={onCloseDeleteTask}
        action={
          <>
            <Button
              className="btn btn-outline-secondary me-3"
              text={t('propertyVisits.addEditView.pageTexts.tasks.modal.deleteCancelBtn')}
              onClick={onCloseDeleteTask}
              disabled={taskLoading}
            />
            <Button
              className="btn btn-danger"
              text={t('propertyVisits.addEditView.pageTexts.tasks.modal.deleteConfirmBtn')}
              onClick={onConfirmDeleteTask}
              loading={taskLoading}
            />
          </>
        }
      />
    </div>
  );
};

export default TasksTab;
